import type { Seedling } from '../lib/types';

interface Props {
  seedling: Seedling;
  tendCount: number;
  onClick?: () => void;
  size?: number;
}

export function SeedlingSprite({ seedling, tendCount, onClick, size = 78 }: Props) {
  const stage = Math.min(3, Math.floor(tendCount / 1.6));
  const warmth = Math.min(1, tendCount / 5);

  const stemH = 26 + stage * 6;
  const leafScale = 0.7 + stage * 0.13;
  const color = `hsl(${140 + warmth * 18}, ${42 + warmth * 18}%, ${38 + warmth * 12}%)`;
  const leafColor = `hsl(${142 + warmth * 10}, ${48 + warmth * 20}%, ${44 + warmth * 8}%)`;

  return (
    <button
      onClick={onClick}
      className="seedling group relative flex flex-col items-center justify-end focus:outline-none"
      style={{ width: size, height: size }}
      aria-label={`Seedling: ${seedling.species}`}
    >
      <svg width={size} height={size} viewBox="0 0 78 78" className={`seedling stage-${stage}`}>
        {/* soil depression */}
        <ellipse cx="39" cy="66" rx="22" ry="6" fill="#D4C9B5" opacity="0.6" />

        {/* stem */}
        <path
          d={`M39 64 Q 37 ${64 - stemH * 0.6} 39 ${64 - stemH}`}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          className="stem"
        />

        {/* main leaf */}
        <g style={{ transform: `translate(39px, ${64 - stemH * 0.65}px)` }}>
          <path
            d={`M0 0 Q ${-12 * leafScale} ${-9 * leafScale} ${-7 * leafScale} ${-18 * leafScale} Q 0 ${-14 * leafScale} ${7 * leafScale} ${-18 * leafScale} Q ${12 * leafScale} ${-9 * leafScale} 0 0`}
            fill={leafColor}
            stroke={color}
            strokeWidth="1"
            className="leaf"
          />
        </g>

        {/* second leaf (more mature) */}
        {stage > 0 && (
          <g style={{ transform: `translate(39px, ${64 - stemH * 0.82}px) rotate(28deg)` }}>
            <path
              d={`M0 0 Q ${-9 * leafScale} ${-7 * leafScale} ${-5 * leafScale} ${-14 * leafScale} Q 0 ${-11 * leafScale} ${5 * leafScale} ${-14 * leafScale} Q ${9 * leafScale} ${-7 * leafScale} 0 0`}
              fill={leafColor}
              stroke={color}
              strokeWidth="0.9"
              className="leaf"
            />
          </g>
        )}

        {/* tiny third leaf / bud for stage 2+ */}
        {stage > 1 && (
          <g style={{ transform: `translate(39px, ${64 - stemH - 2}px)` }}>
            <circle cx="0" cy="-3" r="2.5" fill={leafColor} stroke={color} strokeWidth="0.7" />
          </g>
        )}

        {/* graft indicator (gold dot) */}
        {tendCount >= 3 && (
          <circle cx="52" cy="38" r="3" fill="#C5A46E" stroke="#8B5E3C" strokeWidth="0.5" />
        )}
      </svg>

      <div className="mt-0.5 text-center">
        <div className="text-[10px] leading-none text-[var(--text-muted)] group-hover:text-[var(--text)] transition">
          {seedling.species}
        </div>
        <div className="text-[9px] text-[var(--text-subtle)] tabular-nums">
          {tendCount} tend
        </div>
      </div>
    </button>
  );
}